import React, { FunctionComponent, useContext, useState } from "react";
import { useI18n } from "../../../../hooks/useI18n";
import { Authenticated } from "../../../../models/User";
import { UserContext } from "../../../../UserProvider";
import { StoreInventoryItem } from "../../../../models/StoreInventory";
import { StoreInventoryItemsState } from "../../../../store/stores/types";
import { thunkFetchStoreInventoryItems } from "../../../../store/stores/actions";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Snackbar,
  TextField,
  Typography,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import CloseIcon from "@material-ui/icons/Close";
import useStyles from "./styles";
import { isOk } from "../../../../result";
import { MinimalStoreInventoryItem } from "../../../../models/StoreInventory";
import { OrderUnit } from "../../../../models/Order";
import { PrioritySelect } from "../../../../components/selects/PrioritySelect";
import { AppState } from "../../../../store";
import { Store } from "../../../../models/Store";
import { parseNumberStrict } from "../../../../utils";
import * as StoreInventoryApi from "../../../../api/storeInventoryApi";

interface StoreInventoryItemEditorProps {
  inventoryItem: MinimalStoreInventoryItem;
  storeId: string;
  onSave: (item: any) => void;
}

export const StoreInventoryItemEditor: FunctionComponent<
  StoreInventoryItemEditorProps
> = ({ inventoryItem, storeId, onSave }) => {
  const classes = useStyles();
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  const [state, setState] = useState(inventoryItem);
  const [showError, setShowError] = useState<boolean>(false);

  const allStores = useSelector<AppState, ReadonlyArray<Store>>(
    s => s.stores.stores
  );

  const store = allStores.find(s => s.id === storeId);

  const renderViewNumber = (input: number | undefined) => {
    if (input === undefined || input < 0) {
      return "";
    }

    return input;
  };

  const parseViewNumber = (input: string): number | undefined => {
    if (!input) {
      return undefined;
    }

    return parseNumberStrict(input);
  };

  const renderOrderUnit = (unit?: OrderUnit): string => {
    switch (unit) {
      case OrderUnit.Dfp:
        return "DFP";
      case OrderUnit.Kfp:
        return "ST/KFP";
      default:
        return "Unknown";
    }
  };

  const disabled = () => {
    return false;
  };

  return (
    <>
      <section>
        {store &&
          currentUser.tenant?.configuration?.features?.digitalPriceTags && (
            <div style={{ marginBottom: 10 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={state.hasPriceTag}
                    onChange={e =>
                      setState({ ...state, hasPriceTag: e.target.checked })
                    }
                  />
                }
                label={t("common.formFields.digitalPriceTagExists")}
              />
              <FormHelperText>
                If <b>checked</b> it will sync with the{" "}
                {t("common.messages.digitalPriceTagsSystem")} (Pricer or
                Imagotag). If you link/unlink a price tag in that system it will
                be added/removed in the product list in the app. Have this{" "}
                <b>unchecked</b> for those special products that do not have
                their own {t("common.messages.digitalPriceTag")}.
              </FormHelperText>
            </div>
          )}

        {store && store.features && store.features.clickNCollect === true && (
          <div style={{ marginBottom: 10 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.isOrderable}
                  onChange={e =>
                    setState({ ...state, isOrderable: e.target.checked })
                  }
                />
              }
              label={t("common.formFields.availableForUsersToOrder")}
            />
            <FormHelperText>
              Only viable if store has home delivery or click-n-collect.
            </FormHelperText>
          </div>
        )}
      </section>
      {allStores && allStores.length > 1 && (
        <div>
          <TextField
            className={classes.field}
            label={t("common.formFields.storePrice")}
            type="number"
            value={renderViewNumber(state.price)}
            helperText={`Current price: ${
              inventoryItem.price
                ? renderViewNumber(inventoryItem.price)
                : "..."
            }`}
            onChange={e =>
              setState({ ...state, price: parseViewNumber(e.target.value) })
            }
          />
          <FormHelperText>
            Set <b>{t("common.formFields.storePrice")}</b> when you want to sell
            a product for different price at different stores
          </FormHelperText>
        </div>
      )}

      {currentUser.tenant?.configuration?.features?.centralOrderingSystem && (
        <>
          <TextField
            className={classes.field}
            label={t("common.formFields.maxOnShelf")}
            type="number"
            value={renderViewNumber(state.maxOnShelf)}
            helperText={`Current max on shelf: ${
              inventoryItem.maxOnShelf
                ? renderViewNumber(inventoryItem.maxOnShelf)
                : "..."
            }`}
            onChange={e =>
              setState({
                ...state,
                maxOnShelf: parseViewNumber(e.target.value)
              })
            }
          />
          <TextField
            className={classes.field}
            label={t("common.formFields.orderThreshold")}
            type="number"
            value={renderViewNumber(state.orderThreshold)}
            helperText={`Current order threshold: ${
              inventoryItem.orderThreshold
                ? renderViewNumber(inventoryItem.orderThreshold)
                : "..."
            }`}
            onChange={e =>
              setState({
                ...state,
                orderThreshold: parseViewNumber(e.target.value)
              })
            }
          />
          <TextField
            className={classes.field}
            label={t("common.formFields.articleNumber")}
            helperText={`Original article number: ${
              inventoryItem.articleNumber ?? "..."
            }`}
            value={state.articleNumber}
            onChange={e =>
              setState({ ...state, articleNumber: e.target.value })
            }
          />
          <FormControl className={classes.field} variant={"standard"}>
            <InputLabel id="order-unit-select-label">
              {t("common.formFields.orderUnit")}
            </InputLabel>
            <Select
              labelId="order-unit-select-label"
              value={state.orderUnit ?? ""}
              onChange={e =>
                setState({
                  ...state,
                  orderUnit: e.target.value
                    ? (e.target.value as OrderUnit)
                    : undefined
                })
              }
            >
              <MenuItem key={"Kfp"} value={OrderUnit.Kfp}>
                {renderOrderUnit(OrderUnit.Kfp)}
              </MenuItem>
              <MenuItem key={"Dfp"} value={OrderUnit.Dfp}>
                {renderOrderUnit(OrderUnit.Dfp)}
              </MenuItem>
            </Select>
            <FormHelperText>{`Current order unit: ${
              inventoryItem.orderUnit ?? "..."
            }`}</FormHelperText>
          </FormControl>
          <PrioritySelect
            className={classes.fieldNoBottomMargin}
            value={state.priority?.id ?? ""}
            onSelected={priority =>
              setState({
                ...state,
                priority: priority
              })
            }
          />
          <FormHelperText>{`Current priority: ${
            inventoryItem.priority?.name ?? "..."
          }`}</FormHelperText>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
        <Button
          style={{ width: 200 }}
          variant="contained"
          color="primary"
          disabled={disabled()}
          onClick={async () => {
            let result;
            if (!state.exists) {
              result = await StoreInventoryApi.add(storeId, {
                ...state,
                priorityId: state.priority?.id ? state.priority?.id : null
              });
            } else {
              result = await StoreInventoryApi.update(storeId, {
                ...state,
                priorityId: state.priority?.id
              });
            }

            setShowError(result.type !== "OK");
            if (result.type === "OK") {
              onSave(state);
            }
          }}
        >
          Save item
        </Button>
      </div>
      {showError && (
        <Alert severity="error" style={{ marginTop: "1rem" }}>
          An error occured when adding or update a product
        </Alert>
      )}
    </>
  );
};
